// src/components/congressional/ConFilterBar.jsx
// Congressional Trade Tracker — standard filter/control bar for every tab
// (Trade Feed, Member Profiles, Committee View, Insights, and any future
// tab). Owner request: one shared component so every tab has the same UX,
// and a new tab can just import this and flip on whichever controls it
// needs, rather than each tab hand-building its own filter row.
//
// Every control is opt-in via an explicit `show*` boolean prop — nothing is
// inferred from whether a value/handler happens to be passed, since that
// makes it too easy to accidentally show a control a tab didn't mean to.
//
// Two exports:
//   ConFilterBar        — the top control bar (chamber/party/ticker/member
//                          search/transaction type/status/page size + Apply/Clear)
//   ConPaginationFooter  — the bottom prev/next + page-size bar, reused across
//                          every paginated list instead of each tab
//                          reimplementing its own prev/next buttons

import Select from '../ui/Select';
import { inputStyle, smallBtn } from './congressShared';

// Standard page-size choices everywhere on this page. 100 is the real
// ceiling (matches backend MAX_PAGE_SIZE) — deliberately NOT offering a
// literal "unbounded all" option. Trade Feed alone has 91,000+ total trades;
// an actually-unlimited fetch there would be a real backend query cost and a
// React rendering problem (tens of thousands of DOM nodes), not just a UI
// choice. If a specific view (e.g. a small committee roster) genuinely never
// exceeds ~100 rows, "100" already behaves like "all" for it in practice.
export const PAGE_SIZE_OPTIONS = [
  { value: '10', label: '10 rows' },
  { value: '25', label: '25 rows' },
  { value: '50', label: '50 rows' },
  { value: '100', label: '100 rows' },
];

// Active/Inactive/All — owner request. Default 'active' everywhere it's
// used (set by the calling tab's own state default, not hardcoded here).
export const STATUS_OPTIONS = [
  { value: 'active', label: 'Active members' },
  { value: 'inactive', label: 'No longer serving' },
  { value: 'all', label: 'All (active + former)' },
];

// Window — owner request, applied consistently across Trade Feed, Member
// Profiles, and Insights. EXTENDED (second owner request, same session):
// exact calendar years (2021-2025) added alongside the relative windows —
// real feedback: "most trade they will not look for all they look for YTD
// and 5 year." Matches VALID_WINDOWS in backend/utils/congressShared.js
// exactly — same 10 values, same order. 2020 has no standalone entry since
// it's the documented floor of the entire dataset — 'all' already means
// "2020 and everything since," not a separate bucket.
export const WINDOW_OPTIONS = [
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '120', label: 'Last 120 days' },
  { value: 'ytd', label: 'Year to date' },
  { value: '2025', label: '2025' },
  { value: '2024', label: '2024' },
  { value: '2023', label: '2023' },
  { value: '2022', label: '2022' },
  { value: '2021', label: '2021' },
  { value: 'all', label: '2020+ (all years)' },
];

// CATEGORY_OPTIONS — owner request: 4 display groups over the real,
// allowlist-confirmed ASSETCATEGORY values. The KEYS here (stocks_options/
// funds/bonds_tbills/crypto_commodities) must match
// backend/utils/congressShared.js's CATEGORY_GROUPS keys exactly — that
// file is authoritative for which real values each group expands to; this
// is display-only, a manually-mirrored label list, same discipline as
// WINDOW_OPTIONS above matching VALID_WINDOWS.
export const CATEGORY_OPTIONS = [
  { value: 'stocks_options', label: 'Stocks & Options' },
  { value: 'funds', label: 'Funds' },
  { value: 'bonds_tbills', label: 'Bonds & T-Bills' },
  { value: 'crypto_commodities', label: 'Crypto & Commodities' },
];

export default function ConFilterBar({
  // Chamber
  showChamber, chamber, onChamberChange,
  // Party
  showParty, party, onPartyChange,
  // Ticker (Trade Feed only)
  showTicker, ticker, onTickerChange,
  // Member name search
  showMemberName, memberName, onMemberNameChange, memberNamePlaceholder = 'Member name',
  // Transaction type (Trade Feed only)
  showTransactionType, transactionType, onTransactionTypeChange,
  // Active/Inactive/All status (Member Profiles primarily, but injectable anywhere)
  showStatus, status, onStatusChange,
  // Window (Trade Feed, Insights)
  showWindow, dateWindow, onWindowChange,
  // Asset category — Stocks & Options / Funds / Bonds & T-Bills / Crypto & Commodities
  showCategory, category, onCategoryChange,
  // Rows per page — shown in this top bar AND independently via
  // ConPaginationFooter at the bottom of the list (owner request: both places)
  showPageSize, pageSize, onPageSizeChange,
  // Actions
  onApply, onClear, applyLabel = 'Apply',
}) {
  return (
    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
      {showChamber && (
        <Select value={chamber} onChange={onChamberChange}
          placeholder="All chambers" options={[{ value: 'house', label: 'House' }, { value: 'senate', label: 'Senate' }]} />
      )}
      {showParty && (
        <Select value={party} onChange={onPartyChange}
          placeholder="All parties" options={[{ value: 'D', label: 'Democrat' }, { value: 'R', label: 'Republican' }, { value: 'I', label: 'Independent' }]} />
      )}
      {showStatus && (
        <Select value={status} onChange={onStatusChange} options={STATUS_OPTIONS} />
      )}
      {showWindow && (
        <Select value={dateWindow} onChange={onWindowChange} options={WINDOW_OPTIONS} />
      )}
      {showCategory && (
        <Select value={category} onChange={onCategoryChange} placeholder="All categories" options={CATEGORY_OPTIONS} />
      )}
      {showTicker && (
        <input value={ticker} onChange={e => onTickerChange(e.target.value.toUpperCase())} placeholder="Ticker" style={{ ...inputStyle, width: '100px' }} />
      )}
      {showMemberName && (
        <input value={memberName} onChange={e => onMemberNameChange(e.target.value)} placeholder={memberNamePlaceholder} style={{ ...inputStyle, width: '180px' }} />
      )}
      {showTransactionType && (
        <Select value={transactionType} onChange={onTransactionTypeChange}
          placeholder="All types" options={['Purchase', 'Sale', 'Exchange']} />
      )}
      {showPageSize && (
        <Select value={String(pageSize)} onChange={onPageSizeChange} options={PAGE_SIZE_OPTIONS} />
      )}
      {onApply && <button onClick={onApply} style={smallBtn('#00d4ff')}>{applyLabel}</button>}
      {onClear && <button onClick={onClear} style={smallBtn('#64748b')}>Clear</button>}
    </div>
  );
}

// ── Bottom-of-list pagination — prev/next + optional page-size selector ──
// Reused across every paginated tab instead of each one reimplementing its
// own prev/next buttons. hiddenCount/guest-lock notice stays in each tab
// itself (TradeFeedTab/MemberProfilesTab's own guest-lock UI differs enough
// — e.g. Trade Feed's "Request Invite" copy — that forcing it into this
// shared component would mean passing through too many one-off strings;
// this component only owns the page/pageSize mechanics, not the guest-lock
// messaging).
export function ConPaginationFooter({ page, totalPages, onPrev, onNext, showPageSize, pageSize, onPageSizeChange }) {
  return (
    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap' }}>
      <button onClick={onPrev} disabled={page <= 1} style={{ ...smallBtn('#00d4ff'), opacity: page <= 1 ? 0.4 : 1 }}>← Prev</button>
      <span style={{ color: '#64748b', fontSize: '0.82rem' }}>{page} / {totalPages}</span>
      <button onClick={onNext} disabled={page >= totalPages} style={{ ...smallBtn('#00d4ff'), opacity: page >= totalPages ? 0.4 : 1 }}>Next →</button>
      {showPageSize && (
        <Select value={String(pageSize)} onChange={onPageSizeChange} options={PAGE_SIZE_OPTIONS} />
      )}
    </div>
  );
}
